import { FileExtension } from "@inrev/common";

import { useEffect, useRef, useState } from "react";
import { Control, FieldPath, FieldValues } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { v4 } from "uuid";

export { noop } from "./noop";
export {
	contractSuretyTypeToString,
	finalBondFormTypeToString,
	formatAnonymousName,
	formatBondAmount,
	formatDateForCsv,
	formatDisplayTitle,
	formatEmpty,
	formatFileName,
	formatFromISODate,
	formatHtmlString,
	formatName,
	formatNameOption,
	formatProjectLocation,
	formatRatio,
	formatSummaryDateFromEmpty,
	nonEmptyOr,
} from "./string";

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const logProps = (props: Record<string, any>) =>
	Object.keys(props).forEach((key) => console.log(`${key}: `, props[key]));

export const asyncSleep = async (milliseconds: number): Promise<void> => {
	return new Promise((res) => {
		setTimeout(() => {
			res();
		}, milliseconds);
	});
};

export const usePendingItems = <TPendingItem extends { id: string }>() => {
	const [pendingItems, _setPendingItems] = useState<TPendingItem[]>([]);

	const addPendingItem = (item: Omit<TPendingItem, "id"> & { id?: string }) => {
		_setPendingItems([...pendingItems, { ...item, id: item.id ?? v4() } as TPendingItem]);
	};

	const removePendingItem = (id: string) => {
		_setPendingItems(pendingItems.filter((item) => item.id !== id));
	};

	const setPendingItems = (items: (Omit<TPendingItem, "id"> & { id?: string })[]) => {
		_setPendingItems(items.map((item) => ({ ...item, id: item.id ?? v4() }) as TPendingItem));
	};

	const clearPendingItems = () => {
		setPendingItems([]);
	};

	return [
		pendingItems,
		addPendingItem,
		removePendingItem,
		setPendingItems,
		clearPendingItems,
	] as const;
};

export const useScrollToId = (block?: ScrollLogicalPosition) => {
	const domReadyRef = useRef<HTMLDivElement>(null);
	const [searchParams] = useSearchParams();
	const scrollToId = searchParams.get("st");

	useEffect(() => {
		if (domReadyRef.current !== null && scrollToId !== null) {
			// Hack: When the dom is loading we need the setTimeout to enure the element is present
			setTimeout(() => {
				const elementToScroll = document.getElementById(scrollToId);
				elementToScroll?.scrollIntoView({
					block: block ?? "center",
					behavior: "smooth",
				});
			}, 100);
		}

		return () => {
			domReadyRef.current = null;
		};
	}, [domReadyRef, scrollToId]);

	return domReadyRef;
};

export const convertControlToOnBlur = <TFieldData extends FieldValues>(
	control: Control<TFieldData>,
	name: FieldPath<TFieldData>,
) => {
	const { register, ..._control } = control;
	return {
		..._control,
		register: () => {
			// biome-ignore lint/suspicious/noExplicitAny: legacy any
			const { onBlur, onChange, ..._register } = register<any>(name);
			return { ..._register, onBlur: onChange, onChange: async () => {} };
		},
	};
};

export const getNameAndExtensionFromFileName = (fileNameWithExtension: string) => {
	const nameChunks = fileNameWithExtension.split(".");
	const extension = nameChunks[nameChunks.length - 1] as FileExtension;
	const name = fileNameWithExtension.slice(0, fileNameWithExtension.length - extension.length - 1);
	return { fileName: name, extension };
};

export const formatNegativesAndLeadingZeros = (value?: string, precision?: number) => {
	if (!value || value === "") return value;
	if (value === ".") return "0.";
	// Fixes for error if user hits backspace on a single digit number
	if (value[0] === ".") return "0" + value;
	const decimalParts = value.split(".");
	// This will strip any leading zeros from the number but still retain the negative sign if it exists
	const beforeDecimalParsed = parseInt(decimalParts[0]).toString();
	const beforeDecimal = beforeDecimalParsed.match(/-{0,1}0{1}|-{0,1}[1-9]{1}[0-9]*|-{1}/)![0];
	if (decimalParts.length > 1) {
		return [beforeDecimal, decimalParts[1].slice(0, precision)].join(".");
	}
	return beforeDecimal;
};

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const boolToYN = (value: any) => (typeof value !== "boolean" ? value : value ? "Yes" : "No");

// biome-ignore lint/suspicious/noExplicitAny: legacy any
export const stripEmpty = (value: any) => {
	if (value === "") return undefined;
	return value;
};
